import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { api } from '../utils/api';
import { toast } from 'sonner';
import { Badge } from '../components/ui/badge';
import EditCaseStudyModal from '../components/EditCaseStudyModal';
import UserBadge from '../components/UserBadge';
import BadgeAvatar from '../components/BadgeAvatar';
import ForumLoginModal from '../components/ForumLoginModal';
import LatestVideosStrip from '../components/LatestVideosStrip';

const Dashboard = ({ user, logout }) => {
  const [caseStudies, setCaseStudies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDifficulty, setFilterDifficulty] = useState('all');
  const [filterTag, setFilterTag] = useState('');
  const [editingCaseStudy, setEditingCaseStudy] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isForumLoginOpen, setIsForumLoginOpen] = useState(false);

  useEffect(() => {
    fetchCaseStudies();
  }, [filterDifficulty, filterTag]);

  const fetchCaseStudies = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterDifficulty !== 'all') params.difficulty = filterDifficulty;
      if (filterTag) params.tag = filterTag;
      if (searchTerm) params.search = searchTerm;
      
      const response = await api.get('/case-studies', { params });
      setCaseStudies(response.data);
    } catch (error) {
      console.error('Error fetching case studies:', error);
      toast.error('Failed to load case studies');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchCaseStudies();
  };

  const allTags = [...new Set(caseStudies.flatMap(cs => cs.tags))];

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getDaysRemaining = (endDate) => {
    const now = new Date();
    const end = new Date(endDate);
    const diff = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    return diff > 0 ? diff : 0;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="container mx-auto px-4 py-4">
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center justify-between">
            <Link to="/dashboard" className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <span className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Playfair Display, serif' }}>TCM Study Club</span>
            </Link>
            <div className="flex items-center space-x-3">
              {user && (
                <Link to="/videos">
                  <Button data-testid="videos-btn" className="bg-red-600 hover:bg-red-700 text-white border-0 hover:shadow-lg transition-all px-5">
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                    </svg>
                    Videos
                  </Button>
                </Link>
              )}
              <Link to="/test/simple">
                <Button data-testid="simple-test-btn" className="difficulty-simple border-0 hover:shadow-lg transition-all px-5">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Simple Test
                </Button>
              </Link>
              <Link to="/test/complex">
                <Button data-testid="complex-test-btn" className="difficulty-complex border-0 hover:shadow-lg transition-all px-5">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  Complex Test
                </Button>
              </Link>
              <Link to="/mock-exam">
                <Button data-testid="mock-exam-btn" className="bg-gradient-to-r from-purple-500 via-pink-500 to-rose-500 hover:from-purple-600 hover:via-pink-600 hover:to-rose-600 text-white border-0 hover:shadow-lg transition-all px-5">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Mock Exam
                </Button>
              </Link>
              {user && (
                <>
                  <Link to="/student">
                    <Button data-testid="student-btn" className="bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white rounded-full px-5">
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      Student
                    </Button>
                  </Link>
                  {user.is_admin && (
                    <>
                      <Link to="/create">
                        <Button data-testid="create-case-study-btn" className="bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 text-white rounded-full px-5">
                          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                          Create
                        </Button>
                      </Link>
                      <Link to="/admin">
                        <Button data-testid="admin-btn" className="bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white rounded-full px-5">
                          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          Admin
                        </Button>
                      </Link>
                    </>
                  )}
                  <Link to={`/profile/${user.id}`}>
                    <BadgeAvatar 
                      commentCount={user.comment_count || 0}
                      size="md"
                      userName={user.name}
                      showLevel={false}
                    />
                  </Link>
                  <Button
                    data-testid="logout-btn"
                    onClick={logout}
                    variant="outline"
                    className="border-gray-300 text-gray-700 hover:bg-gray-100 rounded-full px-6"
                  >
                    Logout
                  </Button>
                </>
              )}
              {!user && (
                <Button
                  onClick={() => setIsForumLoginOpen(true)}
                  className="bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 text-white rounded-full px-6"
                >
                  Forum Login
                </Button>
              )}
            </div>
          </div>

          {/* Mobile Navigation */}
          <div className="md:hidden">
            <div className="flex items-center justify-between mb-3">
              <Link to="/dashboard" className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </div>
                <span className="text-lg font-bold text-gray-900" style={{ fontFamily: 'Playfair Display, serif' }}>TCM Study Club</span>
              </Link>
              {user && (
                <Link to={`/profile/${user.id}`}>
                  <BadgeAvatar 
                    commentCount={user.comment_count || 0}
                    size="sm"
                    userName={user.name}
                    showLevel={false}
                  />
                </Link>
              )}
            </div>
            
            {/* Mobile Button Grid */}
            <div className="grid grid-cols-2 gap-2">
              {user && (
                <Link to="/videos" className="w-full col-span-2">
                  <Button data-testid="videos-btn-mobile" className="bg-red-600 hover:bg-red-700 text-white rounded-lg border-0 w-full text-sm py-2">
                    ▶️ Videos
                  </Button>
                </Link>
              )}
              <Link to="/test/simple" className="w-full">
                <Button data-testid="simple-test-btn-mobile" className="difficulty-simple border-0 w-full text-sm py-2">
                  📘 Simple Test
                </Button>
              </Link>
              <Link to="/test/complex" className="w-full">
                <Button data-testid="complex-test-btn-mobile" className="difficulty-complex border-0 w-full text-sm py-2">
                  📕 Complex Test
                </Button>
              </Link>
              <Link to="/mock-exam" className="w-full col-span-2">
                <Button data-testid="mock-exam-btn-mobile" className="bg-gradient-to-r from-purple-500 via-pink-500 to-rose-500 text-white rounded-lg border-0 w-full text-sm py-2">
                  📋 Case Study Mock Exam
                </Button>
              </Link>
              {user && (
                <>
                  <Link to="/student" className="w-full">
                    <Button data-testid="student-btn-mobile" className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-lg w-full text-sm py-2">
                      👤 Student
                    </Button>
                  </Link>
                  {user.is_admin && (
                    <>
                      <Link to="/create" className="w-full">
                        <Button data-testid="create-case-study-btn-mobile" className="bg-gradient-to-r from-teal-500 to-cyan-600 text-white rounded-lg w-full text-sm py-2">
                          ➕ Create
                        </Button>
                      </Link>
                      <Link to="/admin" className="w-full col-span-2">
                        <Button data-testid="admin-btn-mobile" className="bg-gradient-to-r from-purple-500 to-pink-600 text-white rounded-lg w-full text-sm py-2">
                          ⚙️ Admin Panel
                        </Button>
                      </Link>
                    </>
                  )}
                  <Button
                    data-testid="logout-btn-mobile"
                    onClick={logout}
                    variant="outline"
                    className="border-gray-300 text-gray-700 rounded-lg w-full text-sm py-2 col-span-2"
                  >
                    Logout
                  </Button>
                </>
              )}
              {!user && (
                <Button
                  onClick={() => setIsForumLoginOpen(true)}
                  className="bg-gradient-to-r from-teal-500 to-cyan-600 text-white rounded-lg w-full text-sm py-2 col-span-2"
                >
                  Forum Login
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Badge System Info Banner - Only for logged in users */}
      {user && (
        <div className="bg-gradient-to-r from-purple-600 via-pink-600 to-indigo-600 text-white">
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center space-x-4">
                <UserBadge postCount={user.comment_count || 0} size="md" showProgress={false} />
                <div>
                  <p className="font-bold text-lg">
                    Level Up by Contributing!
                  </p>
                  <p className="text-sm text-purple-100">
                    Share your clinical insights and earn badges • {user.comment_count || 0} discussion posts
                  </p>
                </div>
              </div>
              <Link to="/student">
                <Button className="bg-white text-purple-600 hover:bg-purple-50 rounded-full px-6 shadow-lg">
                  View My Progress →
                </Button>
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        {/* Search and Filters */}
        <div className="mb-8 bg-white rounded-2xl p-6 shadow-md">
          <form onSubmit={handleSearch} className="mb-4">
            <div className="flex gap-3">
              <Input
                data-testid="search-input"
                type="text"
                placeholder="Search case studies..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1 rounded-full border-gray-300 focus:border-teal-500 focus:ring-teal-500"
              />
              <Button type="submit" data-testid="search-btn" className="bg-gradient-to-r from-teal-500 to-cyan-600 text-white rounded-full px-8">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                Search
              </Button>
            </div>
          </form>
          
          <div className="flex flex-wrap gap-3 items-center">
            <span className="text-sm font-medium text-gray-700">Filter by:</span>
            <div className="flex gap-2">
              <button
                data-testid="filter-all"
                onClick={() => setFilterDifficulty('all')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  filterDifficulty === 'all'
                    ? 'bg-gradient-to-r from-teal-500 to-cyan-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All
              </button>
              <button
                data-testid="filter-simple"
                onClick={() => setFilterDifficulty('simple')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  filterDifficulty === 'simple'
                    ? 'difficulty-simple'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Simple
              </button>
              <button
                data-testid="filter-complex"
                onClick={() => setFilterDifficulty('complex')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  filterDifficulty === 'complex'
                    ? 'difficulty-complex'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Complex
              </button>
            </div>
            {allTags.length > 0 && (
              <>
                <span className="text-sm text-gray-500 ml-4">Tags:</span>
                <div className="flex gap-2 flex-wrap">
                  {allTags.slice(0, 5).map(tag => (
                    <button
                      key={tag}
                      onClick={() => setFilterTag(filterTag === tag ? '' : tag)}
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                        filterTag === tag
                          ? 'bg-cyan-500 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      #{tag}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Latest Videos Strip */}
        <LatestVideosStrip user={user} />

        {/* Case Studies Grid */}
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="bg-white rounded-2xl p-6 shadow-md">
                <div className="skeleton h-6 w-3/4 mb-4 rounded"></div>
                <div className="skeleton h-4 w-full mb-2 rounded"></div>
                <div className="skeleton h-4 w-5/6 mb-4 rounded"></div>
                <div className="skeleton h-8 w-1/3 rounded-full"></div>
              </div>
            ))}
          </div>
        ) : caseStudies.length === 0 ? (
          <div className="text-center py-16">
            <svg className="w-20 h-20 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="text-gray-600 text-lg mb-4">No case studies found</p>
            <Link to="/create">
              <Button className="bg-gradient-to-r from-teal-500 to-cyan-600 text-white rounded-full px-8">
                Create the first case study
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {caseStudies.map((caseStudy) => (
              <div key={caseStudy.id} className="relative" data-testid={`case-study-card-${caseStudy.id}`}>
                <Link to={`/case-study/${caseStudy.id}`}>
                  <div className="bg-white rounded-2xl p-6 shadow-md hover:shadow-2xl transition-all duration-300 card-hover h-full flex flex-col border border-gray-100">
                    <div className="flex items-start justify-between mb-4">
                      <Badge className={`${caseStudy.difficulty === 'simple' ? 'difficulty-simple' : 'difficulty-complex'} border-0 text-sm px-3 py-1`}>
                        {caseStudy.difficulty === 'simple' ? '📘 Simple' : '📕 Complex'}
                      </Badge>
                      <div className="flex items-center text-sm text-gray-500 bg-gray-50 px-2 py-1 rounded-full">
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        {caseStudy.view_count}
                      </div>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3 line-clamp-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                      {caseStudy.title}
                    </h3>
                    <p className="text-gray-600 text-base mb-4 line-clamp-3 flex-grow leading-relaxed">
                      {caseStudy.description}
                    </p>
                    {caseStudy.tags.length > 0 && (
                      <div className="flex gap-2 flex-wrap mb-4">
                        {caseStudy.tags.slice(0, 3).map(tag => (
                          <span key={tag} className="text-xs bg-gradient-to-r from-cyan-50 to-teal-50 text-teal-700 px-3 py-1 rounded-full font-medium">
                            #{tag}
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="pt-4 border-t border-gray-100 space-y-3">
                      <div className="flex items-center justify-between text-sm">
                        <span className="flex items-center text-gray-600">
                          <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          {caseStudy.author_name}
                        </span>
                        <span className="flex items-center text-teal-600 font-semibold bg-teal-50 px-2 py-1 rounded-full">
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
                          </svg>
                          {caseStudy.upvotes}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">{formatDate(caseStudy.created_at)}</span>
                        {getDaysRemaining(caseStudy.discussion_period_ends) > 0 ? (
                          <span className="text-xs font-semibold text-orange-600 bg-orange-50 px-2 py-1 rounded-full">
                            ⏱ {getDaysRemaining(caseStudy.discussion_period_ends)} days left
                          </span>
                        ) : (
                          <span className="text-xs font-semibold text-green-600 bg-green-50 px-2 py-1 rounded-full">
                            ✓ Solution available
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </Link>
                {/* Edit Icon Button */}
                {(user?.is_admin || user?.is_moderator || caseStudy.author_id === user?.id) && (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setEditingCaseStudy(caseStudy);
                      setIsEditModalOpen(true);
                    }}
                    className="absolute top-4 right-4 p-2 bg-white rounded-full shadow-md hover:shadow-lg transition-all duration-200 text-blue-600 hover:text-blue-700 hover:scale-110 z-10"
                    title="Edit case study"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Edit Modal */}
      <EditCaseStudyModal
        caseStudy={editingCaseStudy}
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditingCaseStudy(null);
        }}
        onSuccess={() => {
          fetchCaseStudies();
          toast.success('Case study updated successfully!');
        }}
      />
      
      {/* Forum Login Modal */}
      <ForumLoginModal
        isOpen={isForumLoginOpen}
        onClose={() => setIsForumLoginOpen(false)}
        onSuccess={(userData) => {
          window.location.reload(); // Reload to update user state
        }}
      />
    </div>
  );
};

export default Dashboard;